/* eslint-disable functional/immutable-data -- ブラウザーの画像・canvas APIを操作する */

export type BrickReferenceImage = {
  dataUrl: string
  name: string
  width: number
  height: number
}

export const BRICK_REFERENCE_IMAGE_MAX_SOURCE_BYTES = 10 * 1024 * 1024
export const BRICK_REFERENCE_IMAGE_MAX_BYTES = 600 * 1024
export const BRICK_REFERENCE_IMAGE_MAX_EDGE = 1600
const MAX_SOURCE_PIXELS = 40_000_000
const JPEG_DATA_URL = /^data:image\/jpeg;base64,([A-Za-z0-9+/]+={0,2})$/
const INVALID_IMAGE =
  "画像を読み込めませんでした。別のJPEG・PNG・WebP画像を選んでください。"

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function safeImageName(name: string): string {
  return (
    Array.from(name)
      .filter((character) => {
        const code = character.charCodeAt(0)
        return code >= 32 && code !== 127 && !(code >= 0x202a && code <= 0x202e)
      })
      .join("")
      .replace(/[\\/]/g, "_")
      .trim()
      .slice(0, 100) || "参考画像"
  )
}

function jpegBytes(dataUrl: string): Uint8Array<ArrayBuffer> | null {
  if (dataUrl.length > Math.ceil(BRICK_REFERENCE_IMAGE_MAX_BYTES / 3) * 4 + 23)
    return null
  const encoded = JPEG_DATA_URL.exec(dataUrl)?.[1]
  if (!encoded || encoded.length % 4 !== 0) return null
  try {
    const decoded = atob(encoded)
    if (
      decoded.length > BRICK_REFERENCE_IMAGE_MAX_BYTES ||
      decoded.length < 4 ||
      decoded.charCodeAt(0) !== 0xff ||
      decoded.charCodeAt(1) !== 0xd8 ||
      decoded.charCodeAt(2) !== 0xff
    )
      return null
    return Uint8Array.from(decoded, (character) => character.charCodeAt(0))
  } catch {
    return null
  }
}

function validImage(value: unknown): value is BrickReferenceImage {
  if (!isRecord(value)) return false
  return (
    typeof value.dataUrl === "string" &&
    typeof value.name === "string" &&
    value.name.length > 0 &&
    value.name === safeImageName(value.name) &&
    Number.isInteger(value.width) &&
    Number.isInteger(value.height) &&
    typeof value.width === "number" &&
    typeof value.height === "number" &&
    value.width >= 1 &&
    value.height >= 1 &&
    value.width <= BRICK_REFERENCE_IMAGE_MAX_EDGE &&
    value.height <= BRICK_REFERENCE_IMAGE_MAX_EDGE &&
    jpegBytes(value.dataUrl) !== null
  )
}

function readBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result)
      else reject(new Error(INVALID_IMAGE))
    }
    reader.onerror = () => reject(new Error(INVALID_IMAGE))
    reader.onabort = () => reject(new Error(INVALID_IMAGE))
    reader.readAsDataURL(blob)
  })
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(INVALID_IMAGE))
    image.src = url
  })
}

function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob?.type === "image/jpeg") resolve(blob)
        else reject(new Error(INVALID_IMAGE))
      },
      "image/jpeg",
      quality,
    )
  })
}

/** ローカル画像を縮小・再描画し、位置情報などの元メタデータを引き継がない。 */
export async function prepareBrickReferenceImage(
  file: File,
): Promise<BrickReferenceImage> {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
    throw new Error("JPEG・PNG・WebP形式の画像を選んでください。")
  if (file.size === 0 || file.size > BRICK_REFERENCE_IMAGE_MAX_SOURCE_BYTES)
    throw new Error("10MB以下の画像を選んでください。")

  const url = URL.createObjectURL(file)
  try {
    const image = await loadImage(url)
    if (
      image.naturalWidth < 1 ||
      image.naturalHeight < 1 ||
      image.naturalWidth * image.naturalHeight > MAX_SOURCE_PIXELS
    )
      throw new Error(
        "画像が大きすぎます。4,000万画素以下に縮小して選び直してください。",
      )

    const ratio = Math.min(
      1,
      BRICK_REFERENCE_IMAGE_MAX_EDGE /
        Math.max(image.naturalWidth, image.naturalHeight),
    )
    const canvas = document.createElement("canvas")
    const context = canvas.getContext("2d")
    if (!context) throw new Error(INVALID_IMAGE)

    // ノイズの多い画像も送信容量内に収める。各段階で元画像から再描画する。
    for (const scale of [1, 0.8, 0.6, 0.4]) {
      canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio * scale))
      canvas.height = Math.max(
        1,
        Math.round(image.naturalHeight * ratio * scale),
      )
      context.fillStyle = "#fff"
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(image, 0, 0, canvas.width, canvas.height)
      for (const quality of [0.86, 0.68, 0.5]) {
        const blob = await canvasBlob(canvas, quality)
        if (blob.size > BRICK_REFERENCE_IMAGE_MAX_BYTES) continue
        const prepared = {
          dataUrl: await readBlob(blob),
          name: safeImageName(file.name),
          width: canvas.width,
          height: canvas.height,
        }
        if (!validImage(prepared)) throw new Error(INVALID_IMAGE)
        return prepared
      }
    }
    throw new Error(
      "画像を送信できる容量に縮小できませんでした。別の画像を選んでください。",
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** 送信直前にだけFileへ変換する。データURLをサーバーURLとして取得しない。 */
export function brickReferenceImageFile(image: BrickReferenceImage): File {
  if (!validImage(image)) throw new Error(INVALID_IMAGE)
  const bytes = jpegBytes(image.dataUrl)
  if (!bytes) throw new Error(INVALID_IMAGE)
  const base = image.name.replace(/\.[^.]*$/, "").slice(0, 96) || "参考画像"
  return new File([bytes], `${base}.jpg`, { type: "image/jpeg" })
}
